import { Injectable, signal } from "@angular/core";
import {
  DaySchedule,
} from "../types/store-settings";
import { StaffInfo, WorkHours } from "../types/staff";
import { ConfigService } from "./ConfigService";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { UriApi } from "../helpers/UriApi";
import { EnumOption } from '../models/EnumOption';
import { AuthenticationService } from "./authentication.service";

const EMPTY_STAFF: StaffInfo = {
  id: "",
  name: "",
  staffNo: "",
  userId: "",
  phone: "",
  email: "",
  type: "",
  role: "",
  status: "",
  joinDate: new Date().toISOString().split("T")[0],
  address: "",
  memo: "",
  workHours: {
    mon: [{ id: 'mon_default', start: '', end: '' }],
    tue: [{ id: 'tue_default', start: '', end: '' }],
    wed: [{ id: 'wed_default', start: '', end: '' }],
    thu: [{ id: 'thu_default', start: '', end: '' }],
    fri: [{ id: 'fri_default', start: '', end: '' }],
    sat: [{ id: 'sat_default', start: '', end: '' }],
    sun: [{ id: 'sun_default', start: '', end: '' }],
  },
};

const DAYS_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

@Injectable({
  providedIn: "root",
})
export class SettingsService {
  api_getData = ConfigService.apiUrl + "/CustomerManagement/GetAll";
  api_postData = ConfigService.apiUrl + "/CustomerManagement/Post";
  api_excute_sp = ConfigService.apiUrl + "/CustomerManagement/execute-sp";
  api_prepaid = ConfigService.apiUrl + "/PrepaidManagement/Post";
  api_staffManagement = ConfigService.apiUrl + "/StaffManagement/Post";
  api_checkUserExist = ConfigService.apiUrl + "/StaffManagement/CheckExistUser";
  api_staffTime = ConfigService.apiUrl + "/StaffManagement/PostTime";
  body = {
    request: {
      jsonParams: {},
      option: EnumOption.GetAll,
      data: "",
      dataList: "",
      menuCd: ''
    }
  };

  constructor(private http: HttpClient, private auth: AuthenticationService) { }

  private weeklySchedule = signal<DaySchedule[]>(
    DAYS_ORDER.map((day) => ({
      day,
      isOpen: !["sun"].includes(day),
      openTime: "10:00",
      closeTime: "20:00",
      breakTimes: [{ id: "1", start: "12:00", end: "13:00" }],
    })),
  );
  getEmptyStaff() {
    return { ...EMPTY_STAFF };
  }

  /**
   * Add or update staff member
   * @param staff Staff data
   * @param storeCode Store code (STR_CD)
   * @param userId User ID format "store001"
   * @param userName Store code for UserName
   */
  addOrUpdateStaff(staff: any, storeCode: string, userId: string, userName: string): Observable<any> {
    const isUpdate = !!staff.EMP_NO;

    const jsonData: any = {
      STR_CD: storeCode,
      USER_ID: staff.USER_ID,
      USER_NM: staff.USER_NM,
      EMP_TP: staff.EMP_TP || "",
      EMP_AUTH: staff.EMP_AUTH || "",
      RETIRE_FG: staff.RETIRE_FG || "",
      TEL_NO: staff.TEL_NO,
      EMAIL: staff.EMAIL || "",
      HR_DT: staff.HR_DT || "",
      POST_NO: staff.POST_NO || "",
      ADDR: staff.ADDR || "",
      ADDR_DTL: staff.ADDR_DTL || "",
    };

    if (!isUpdate && staff.PASSWORD) {
      jsonData.PASSWORD = staff.PASSWORD;
    }

    if (isUpdate) {
      jsonData.EMP_NO = staff.EMP_NO;
    }

    if (staff.RETIRE_DT) {
      jsonData.RETIRE_DT = staff.RETIRE_DT;
    }
    if (staff.REMARK) {
      jsonData.REMARK = staff.REMARK;
    }

    const payload = {
      Request: {
        UserId: userId,
        UserName: userName,
        JsonParams: {
          JsonData: jsonData
        },
        FunctionCode: 100,
        Option: 1,
        CommandType: 1,
        Data: null,
        DataList: []
      }
    };

    return this.http.post(this.api_staffManagement, payload);
  }

  checkUserIdExists(userID: string): Observable<any> {
    return this.http.get(`${this.api_checkUserExist}?userID=${userID}`);
  }

  saveStaffWorkTimes(
    storeCode: string,
    userId: string,
    workTimes: Array<{ day: string, start_time: string, end_time: string, DAY_SEQ?: string }>,
    loginUserId: string,
    loginUserName: string
  ): Observable<any> {
    const payload = {
      Request: {
        UserId: loginUserId,
        UserName: loginUserName,
        JsonParams: null,
        Data: {
          STR_CD: storeCode,
          USER_ID: userId,
          WORKTIMES: workTimes
        },
        FunctionCode: 100,
        Option: 1,
        CommandType: 1,
        DataList: []
      }
    };

    return this.http.post(this.api_staffTime, payload);
  }

  /**
   * Save staff info with work times in single API call
   * @param staffData Staff data including WORKTIMES array
   * @param loginUserId User ID for login
   * @param loginUserName User name for login
   */
  saveStaffWithWorkTimes(
    staffData: any,
    loginUserId: string,
    loginUserName: string
  ): Observable<any> {
    const payload = {
      Request: {
        UserId: loginUserId,
        UserName: loginUserName,
        JsonParams: null,
        Data: staffData,
        FunctionCode: 100,
        Option: 1,
        CommandType: 1,
        DataList: []
      }
    };

    return this.http.post(this.api_staffTime, payload);
  }

  getWeeklySchedule() {
    return this.weeklySchedule.asReadonly();
  }

  postCovers(covers: any) {
    const formData = new FormData();
    covers.forEach((f: any) => {
      const file = f instanceof File ? f : f?.file;
      if (!file) return;

      formData.append('covers', file, file.name);
    });

    return this.http.post(UriApi.api_store_postCovers, formData);
  }

  postRep(representative: any) {
    const formData = new FormData();
    if (representative) {
      const file = representative instanceof File ? representative : representative?.file;
      if (!file) return;
      formData.append('rep', file, file.name);
    }

    return this.http.post(UriApi.api_store_postRep, formData);
  }

  post(param: any = {}) {
    const formData = new FormData();

    formData.append("request", JSON.stringify({
      jsonParams: param,
      functionCode: 100,
      option: EnumOption.GetAll,
      data: "",
      dataList: "",
      menuCd: '',
    }));

    return this.http.post(UriApi.api_store_setting, formData);
  }

  updateStoreMemberShip(param: any) {
    this.body.request.jsonParams = param;
    return this.http.post(UriApi.api_store_update_membership, this.body);
  }

  memberLevelPost(param: any, option: EnumOption) {
    this.body.request.jsonParams = param;
    this.body.request.option = option;
    return this.http.post(UriApi.api_store_MemberLevelPost, this.body);
  }

  memberPricePost(param: any, option: EnumOption) {
    this.body.request.jsonParams = param;
    this.body.request.option = option;
    return this.http.post(UriApi.api_store_MemberPricePost, this.body);
  }

  timePost(param: any) {
    this.body.request.jsonParams = param;
    return this.http.post(UriApi.api_store_timePost, this.body);
  }

  holidayPost(param: any) {
    this.body.request.jsonParams = param;
    return this.http.post(UriApi.api_store_holidayPost, this.body);
  }

  postStaff(param: any) {
    this.body.request.jsonParams = param;
    return this.http.post(UriApi.api_store_postStaff, this.body);
  }

  postStaffTime(param: any) {
    this.body.request.jsonParams = param;
    return this.http.post(UriApi.api_store_postStaffTime, this.body);
  }

  deleteStaff(param: any) {
    this.body.request.jsonParams = param;
    return this.http.post(UriApi.api_store_deleteStaff, this.body);
  }


  private SECRET_KEY = this.auth.getPrivateKeyUser();

  encode(input: string): string {
    const encoder = new TextEncoder();
    let bytes = encoder.encode(input);
    let key = encoder.encode(this.SECRET_KEY);

    for (let i = 0; i < bytes.length; i++) {
      bytes[i] ^= key[i % key.length];
    }

    let binary = '';
    bytes.forEach(b => binary += String.fromCharCode(b));
    let base64 = btoa(binary);

    return base64.match(/.{1,4}/g)?.join('-') ?? base64;
  }

  decode(keyFormatted: string): string {
    const raw = keyFormatted.replace(/-/g, '');

    const binary = atob(raw);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    const encoder = new TextEncoder();
    const key = encoder.encode(this.SECRET_KEY);

    for (let i = 0; i < bytes.length; i++) {
      bytes[i] ^= key[i % key.length];
    }

    const decoder = new TextDecoder();
    return decoder.decode(bytes);
  }

  updateAgent(){
    return this.http.post(UriApi.api_store_updateAgent, this.body);
  }
}
